import { Link } from 'react-router-dom'
import type { ControlledSubstanceEntry } from '../../store/controlledSubstancesSlice'

interface ControlledSubstancesTableProps {
  entries: ControlledSubstanceEntry[]
}

function formatRxNumber(num: number | null): string {
  if (num == null) return ''
  return `RX-${String(num).padStart(4, '0')}`
}

function formatDateTime(dateStr: string): string {
  const d = new Date(dateStr)
  return d.toLocaleString('en-US', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

function transactionTypeBadge(type: string) {
  const colors: Record<string, string> = {
    Received: 'bg-accent-subtle-bg text-accent-dark',
    Dispensed: 'bg-[#e8e0d0] text-[#8a6b30]',
    Wasted: 'bg-[#f0dede] text-status-error',
    Adjustment: 'bg-[#e0e4e8] text-status-info',
  }
  return (
    <span
      data-testid="transaction-type-badge"
      className={`inline-flex items-center h-5 px-1.5 rounded-[3px] text-[11px] font-medium ${colors[type] || 'bg-border text-text-muted'}`}
    >
      {type}
    </span>
  )
}

export function ControlledSubstancesTable({ entries }: ControlledSubstancesTableProps) {
  if (entries.length === 0) {
    return (
      <div data-testid="controlled-substances-table" className="flex flex-col">
        <p className="text-[12px] text-text-muted py-8 text-center">No log entries found.</p>
      </div>
    )
  }

  return (
    <div data-testid="controlled-substances-table" className="flex flex-col">
      {/* Header row */}
      <div
        className="cs-table-header grid items-center h-9 text-[11px] font-medium text-text-muted uppercase tracking-wide"
        style={{
          gridTemplateColumns: '140px 1fr 80px 100px 70px 120px 100px 120px 120px 90px',
        }}
      >
        <span className="px-2">Date/Time</span>
        <span className="px-2">Medication Name</span>
        <span className="px-2">DEA</span>
        <span className="px-2">Type</span>
        <span className="px-2 text-right">Qty</span>
        <span className="px-2">Patient Name</span>
        <span className="px-2">Rx Number</span>
        <span className="px-2">Performed By</span>
        <span className="px-2">Witnessed By</span>
        <span className="px-2 text-right">Balance</span>
      </div>

      {/* Data rows */}
      {entries.map((entry) => (
        <div
          key={entry.id}
          data-testid={`cs-log-row-${entry.id}`}
          className="cs-table-row grid items-center h-[44px] text-[13px] text-text-primary hover:bg-hover transition-colors duration-100"
          style={{
            gridTemplateColumns: '140px 1fr 80px 100px 70px 120px 100px 120px 120px 90px',
          }}
        >
          <span className="px-2 text-[12px] text-text-muted truncate" data-testid="cs-date">
            {formatDateTime(entry.transaction_date)}
          </span>
          <span className="px-2 font-[450] truncate" data-testid="cs-medication-name">
            {entry.medication_name}
          </span>
          <span className="px-2 text-[12px] text-text-muted" data-testid="cs-dea-schedule">
            {entry.dea_schedule ? `Schedule ${entry.dea_schedule}` : '\u2014'}
          </span>
          <span className="px-2" data-testid="cs-transaction-type">
            {transactionTypeBadge(entry.transaction_type)}
          </span>
          <span className="px-2 text-right tabular-nums" data-testid="cs-quantity">
            {entry.transaction_type === 'Dispensed' || entry.transaction_type === 'Wasted'
              ? `-${entry.quantity}`
              : entry.transaction_type === 'Adjustment'
                ? (entry.quantity > 0 ? `+${entry.quantity}` : `${entry.quantity}`)
                : `+${entry.quantity}`}
          </span>
          <span className="px-2 truncate" data-testid="cs-patient-name">
            {entry.patient_id && entry.patient_name ? (
              <Link
                to={`/patients/${entry.patient_id}`}
                className="text-accent hover:underline"
                data-testid="cs-patient-link"
              >
                {entry.patient_name}
              </Link>
            ) : (
              <span className="text-text-muted">{'\u2014'}</span>
            )}
          </span>
          <span className="px-2 truncate" data-testid="cs-rx-number">
            {entry.prescription_id && entry.prescription_number ? (
              <Link
                to={`/prescriptions/${entry.prescription_id}`}
                className="text-accent hover:underline"
                data-testid="cs-rx-link"
              >
                {formatRxNumber(entry.prescription_number)}
              </Link>
            ) : (
              <span className="text-text-muted">{'\u2014'}</span>
            )}
          </span>
          <span className="px-2 text-[12px] truncate" data-testid="cs-performed-by">
            {entry.performed_by}
          </span>
          <span className="px-2 text-[12px] truncate" data-testid="cs-witnessed-by">
            {entry.witnessed_by || <span className="text-text-muted">{'\u2014'}</span>}
          </span>
          <span className="px-2 text-right font-medium tabular-nums" data-testid="cs-running-balance">
            {entry.running_balance}
          </span>
        </div>
      ))}
    </div>
  )
}
